import { defaultTravelPaymentValues } from "@/D2C/features/travel-quote/redux/travel-quote/travelQuoteSlice";
import type { TravelPaymentValues } from "@/D2C/features/travel-quote/redux/type";
import type { RootState } from "@/shared/redux/store/store";
import type { PersistedUploadedDocument } from "@/shared/types";
import { getCurrentCountryKey, LOCALISATION_PROFILE } from "@/shared/config/localisation";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { LIFE_QUOTE_STEP_KEYS } from "../common/static";

export type LifeDocumentUploadValues = {
  aadhaarCard?: boolean;
  panCard?: boolean;
  emiratesIdFront?: boolean;
  emiratesIdBack?: boolean;
  emiratesIdCombined?: boolean;
  confirmDocuments: boolean;
  [key: string]: boolean;
};

export type LifeDocumentUploadFieldKey = string;

export type LifeUploadedDocumentMap = Partial<
  Record<LifeDocumentUploadFieldKey, PersistedUploadedDocument<LifeDocumentUploadFieldKey>>
>;

export type LifePlanDetailsFormValues = {
  lifeAssuredDob: string;
  lifeAssuredGender: "male" | "female" | "";
  smoker: "yes" | "no" | "";
  planOption: string;
  sumAssured: string;
  policyTerm: string;
  premiumPaymentTerm: string;
  premiumPaymentFrequency: string;
  category: string;
  payoutType: string;
  payoutTerm?: string;
  lumpsumPercentage?: string;
  accidentalDeathSumAssured?: string;
};

export type LifeQuoteState = {
  planDetails: LifePlanDetailsFormValues;
  quoteSelection: { selectedQuoteId: string };
  documentUpload: LifeDocumentUploadValues;
  documentUploadDocuments: LifeUploadedDocumentMap;
  // additionalInfo: TravelAdditionalInfoValues;
  payment: TravelPaymentValues;
};

export type LifeQuoteStepState = {
  [LIFE_QUOTE_STEP_KEYS.TERM_DETAILS]: LifePlanDetailsFormValues;
  [LIFE_QUOTE_STEP_KEYS.QUOTE_SELECTION]: LifeQuoteState["quoteSelection"];
  [LIFE_QUOTE_STEP_KEYS.DOCUMENT_UPLOAD]: LifeQuoteState["documentUpload"];
  [LIFE_QUOTE_STEP_KEYS.PAYMENT]: LifeQuoteState["payment"];
};

export type LifeQuoteStepKey = keyof LifeQuoteStepState;

export type SaveLifeQuoteStepPayload = {
  [TStepKey in LifeQuoteStepKey]: {
    stepName: TStepKey;
    data: LifeQuoteStepState[TStepKey];
  };
}[LifeQuoteStepKey];

function createSaveLifeQuoteStepPayload<TStepKey extends LifeQuoteStepKey>(
  stepName: TStepKey,
  data: LifeQuoteStepState[TStepKey]
): Extract<SaveLifeQuoteStepPayload, { stepName: TStepKey }> {
  return { stepName, data } as Extract<SaveLifeQuoteStepPayload, { stepName: TStepKey }>;
}

export function saveLifeQuoteStepFor<TStepKey extends LifeQuoteStepKey>(
  stepName: TStepKey,
  data: LifeQuoteStepState[TStepKey]
) {
  return saveLifeQuoteStep(createSaveLifeQuoteStepPayload(stepName, data));
}

export const defaultLifeDocumentUploadValues: LifeDocumentUploadValues = {
  ...Object.fromEntries(
    LOCALISATION_PROFILE.lifeDocuments.flatMap(({ key }) =>
      getCurrentCountryKey() === "UAE" && key === "emiratesId"
        ? [
            ["emiratesIdFront", false],
            ["emiratesIdBack", false],
            ["emiratesIdCombined", false],
          ]
        : [[key, false]]
    )
  ),
  confirmDocuments: false,
};

export const defaultLifePlanDetailsFormValues: LifePlanDetailsFormValues = {
  lifeAssuredDob: "",
  lifeAssuredGender: "male",
  smoker: "no",
  planOption: "life_cover",
  sumAssured: "",
  policyTerm: "",
  premiumPaymentTerm: "",
  premiumPaymentFrequency: "",
  category: "",
  payoutType: "lumpsum",
  payoutTerm: "",
  lumpsumPercentage: "",
  accidentalDeathSumAssured: "",
};

export const defaultLifeQuoteState: LifeQuoteState = {
  planDetails: defaultLifePlanDetailsFormValues,
  quoteSelection: { selectedQuoteId: "" },
  documentUpload: defaultLifeDocumentUploadValues,
  documentUploadDocuments: {},
  // additionalInfo: defaultTravelAdditionalInfoValues,
  payment: defaultTravelPaymentValues,
};

const lifeQuoteSlice = createSlice({
  name: "lifeQuote",
  initialState: defaultLifeQuoteState,
  reducers: {
    saveLifeQuoteStep(state, action: PayloadAction<SaveLifeQuoteStepPayload>) {
      switch (action.payload.stepName) {
        case LIFE_QUOTE_STEP_KEYS.TERM_DETAILS:
          state.planDetails = action.payload.data;
          break;
        case LIFE_QUOTE_STEP_KEYS.QUOTE_SELECTION:
          state.quoteSelection = action.payload.data;
          break;
        case LIFE_QUOTE_STEP_KEYS.DOCUMENT_UPLOAD:
          state.documentUpload = action.payload.data;
          break;
        // case LIFE_QUOTE_STEP_KEYS.PAYMENT:
        //   state.payment = action.payload.data;
        //   break;
      }
    },
    saveLifeUploadedDocument(
      state,
      action: PayloadAction<{
        fieldKey: LifeDocumentUploadFieldKey;
        document: LifeUploadedDocumentMap[LifeDocumentUploadFieldKey];
      }>
    ) {
      const { fieldKey, document } = action.payload;

      if (!document) {
        delete state.documentUploadDocuments[fieldKey];
        return;
      }

      state.documentUploadDocuments[fieldKey] = document;
    },
    // saveLifeAdditionalInfo(state, action: PayloadAction<LifeQuoteState["additionalInfo"]>) {
    //   state.additionalInfo = action.payload;
    // },
    resetLifeQuoteState() {
      return defaultLifeQuoteState;
    },
  },
});

export const {
  saveLifeQuoteStep,
  saveLifeUploadedDocument,
  // saveLifeAdditionalInfo,
  resetLifeQuoteState,
} = lifeQuoteSlice.actions;

export const selectLifeQuoteState = (state: RootState) => state.lifeQuote;
export const selectLifeUploadedDocuments = (state: RootState) =>
  state.lifeQuote.documentUploadDocuments;

export default lifeQuoteSlice.reducer;
